import {
  collection,
  Firestore,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  limit,
  startAfter,
  getDoc,
  doc,
  updateDoc,
  setDoc,
  getCount,
  getAggregate,
  sum,
} from "firebase/firestore/lite";
import {
  APIMembershipPricing,
  DbBusinessMembership,
  DbBusinessMembershipTemplate,
  DbMembershipService,
  DbMembershipStatus,
} from "./models";
import { firebaseDb } from "../firebase/client-app";
import { axiosInstance } from "../rest/axios";
import { getCookie } from "cookies-next";
import { DbBusinessPaymentType } from "../business/models";

export class MembershipService {
  private businessMembershipRef(firestore: Firestore) {
    return collection(firestore, "business_memberships");
  }

  private membershipTemplateRef(businessId: string, firestore: Firestore) {
    return collection(
      firestore,
      `businesses/${businessId}/business_membership_templates`,
    );
  }

  generateMembershipTemplateId(
    businessId: string,
    firestore: Firestore,
  ): string {
    return doc(this.membershipTemplateRef(businessId, firestore)).id;
  }

  async getUserMemberships(
    userId: string,
    lastMembership?: DbBusinessMembership | null,
    pageSize: number = 12,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessMembership[]> {
    let q = query(
      this.businessMembershipRef(firestore),
      where("user_id", "==", userId),
      orderBy("created_at", "desc"),
      limit(pageSize),
    );

    if (lastMembership) {
      q = query(q, startAfter(lastMembership.created_at));
    }

    const userMembershipsSnapshot = await getDocs(q);
    return userMembershipsSnapshot.docs.map(
      (doc) => doc.data() as DbBusinessMembership,
    );
  }

  async getUserMembershipsByBusiness(
    userId: string,
    businessId: string,
    lastMembership?: DbBusinessMembership | null,
    pageSize: number = 10,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessMembership[]> {
    let q = query(
      this.businessMembershipRef(firestore),
      where("user_id", "==", userId),
      where("business_id", "==", businessId),
      orderBy("created_at", "desc"),
      limit(pageSize),
    );

    if (lastMembership) {
      q = query(q, startAfter(lastMembership.created_at));
    }

    const userMembershipsSnapshot = await getDocs(q);
    return userMembershipsSnapshot.docs.map(
      (doc) => doc.data() as DbBusinessMembership,
    );
  }

  async getUserActiveMembershipsByBusiness(
    userId: string,
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessMembership[]> {
    const userMembershipsSnapshot = await getDocs(
      query(
        this.businessMembershipRef(firestore),
        where("user_id", "==", userId),
        where("business_id", "==", businessId),
        orderBy("created_at", "desc"),
      ),
    );
    return userMembershipsSnapshot.docs
      .map((doc) => doc.data() as DbBusinessMembership)
      .filter((membership) => {
        const startAt = membership.start_at ?? membership.created_at;
        return startAt < Timestamp.now() && membership.expiry > Timestamp.now();
      });
  }

  async getBusinessMembershipTemplates(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessMembershipTemplate[]> {
    const templatesSnapshot = await getDocs(
      query(
        this.membershipTemplateRef(businessId, firestore),
        where("status", "==", DbMembershipStatus.enable),
      ),
    );
    return templatesSnapshot.docs.map(
      (doc) => doc.data() as DbBusinessMembershipTemplate,
    );
  }

  async getBusinessMembershipAllTemplates(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessMembershipTemplate[]> {
    const templatesSnapshot = await getDocs(
      query(
        this.membershipTemplateRef(businessId, firestore),
        where("status", "!=", DbMembershipStatus.deleted),
      ),
    );
    return templatesSnapshot.docs.map(
      (doc) => doc.data() as DbBusinessMembershipTemplate,
    );
  }

  async getBusinessMembershipPricing(
    businessId: string,
    templateId: string,
  ): Promise<APIMembershipPricing> {
    const res = await axiosInstance.get(
      `user/business/${businessId}/membership/template/${templateId}/pricing`,
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );
    return res.data;
  }

  async getMembershipById(
    id: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessMembership> {
    const membershipDoc = await getDoc(
      doc(this.businessMembershipRef(firestore), id),
    );

    return membershipDoc.data() as DbBusinessMembership;
  }

  async getBusinessMembershipTemplateById(
    id: string,
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessMembershipTemplate> {
    const membershipDoc = await getDoc(
      doc(this.membershipTemplateRef(businessId, firestore), id),
    );
    return membershipDoc.data() as DbBusinessMembershipTemplate;
  }

  async confirmMembershipPurchase(
    businessId: string,
    templateId: string,
    paymentId: string | null,
    clientId: string | null,
    adjusted_price?: number | null,
    adjusted_benefit_amount?: number | null,
    adjusted_services?: DbMembershipService[],
    adjusted_start_at?: Timestamp | null,
    adjusted_expiry?: Timestamp | null,
  ): Promise<DbBusinessMembership> {
    const response = await axiosInstance.post(
      "user/business/membership",
      {
        business_id: businessId,
        template_id: templateId,
        payment_id: paymentId,
        client_id: clientId,
        adjusted_services: adjusted_services,
        adjusted_start_at: adjusted_start_at,
        adjusted_price: adjusted_price,
        adjusted_benefit_amount: adjusted_benefit_amount,
        adjusted_expiry: adjusted_expiry,
      },
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );

    return response.data;
  }

  async getBusinessMembershipCounts(
    businessId: string,
    startDate?: Timestamp | null,
    endDate?: Timestamp | null,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    let q = query(
      this.businessMembershipRef(firestore),
      where("business_id", "==", businessId),
    );

    if (startDate && endDate) {
      q = query(
        q,
        where("created_at", ">=", startDate),
        where("created_at", "<=", endDate),
      );
    }

    const memberships = await getCount(q);
    return memberships.data().count;
  }

  async saveTemplate(
    businessId: string,
    data: DbBusinessMembershipTemplate,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    data.id = this.generateMembershipTemplateId(businessId, firestore);
    data.created_at = Timestamp.now();
    try {
      await setDoc(
        doc(this.membershipTemplateRef(businessId, firestore), data.id),
        data,
      );
    } catch (err: any) {
      throw err;
    }
  }

  async updateTemplate(
    id: string,
    businessId: string,
    data: DbBusinessMembershipTemplate,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    try {
      await updateDoc(
        doc(this.membershipTemplateRef(businessId, firestore), id),
        {
          name: data.name,
          price: data.price,
          mandatory_advance_percentage: data.mandatory_advance_percentage,
          image_url: data.image_url,
          business_id: data.business_id,
          description: data.description,
          duration: data.duration,
          type: data.type,
          status: data.status,
          benefit_amount: data.benefit_amount,
          services2: data.services2,
          updated_at: Timestamp.now(),
        },
      );
    } catch (err: any) {
      throw err;
    }
  }

  async updateTemplateStatus(
    id: string,
    businessId: string,
    status: DbMembershipStatus,
    firestore: Firestore = firebaseDb,
  ) {
    await updateDoc(
      doc(this.membershipTemplateRef(businessId, firestore), id),
      {
        status: status,
      },
    );
  }

  async getAllBusinessMemberships(
    businessId: string,
    lastMembership?: DbBusinessMembership | null,
    pageSize: number = 10,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessMembership[]> {
    let q = query(
      this.businessMembershipRef(firestore),
      where("business_id", "==", businessId),
      orderBy("created_at", "desc"),
      limit(pageSize),
    );

    if (lastMembership) {
      q = query(q, startAfter(lastMembership.created_at));
    }
    const membershipsSnapshot = await getDocs(q);

    return membershipsSnapshot.docs.map(
      (doc) => doc.data() as DbBusinessMembership,
    );
  }

  async addMembershipCashPayment(
    businessId: string,
    membershipId: string,
    type: DbBusinessPaymentType,
    amount: number,
  ): Promise<DbBusinessMembership> {
    const response = await axiosInstance.post(
      "/user/business/membership/payment",
      {
        business_id: businessId,
        membership_id: membershipId,
        payment_type: type,
        amount: amount,
      },
      {
        headers: {
          Authorization: getCookie("Authorization")?.toString(),
        },
      },
    );
    return response.data as DbBusinessMembership;
  }

  async undoMembershipCashPayment(
    businessId: string,
    membershipId: string,
  ): Promise<DbBusinessMembership> {
    const response = await axiosInstance.delete(
      "/user/business/membership/payment",
      {
        data: {
          business_id: businessId,
          membership_id: membershipId,
        },
        headers: {
          Authorization: getCookie("Authorization")?.toString(),
        },
      },
    );
    return response.data as DbBusinessMembership;
  }

  async getMembershipRevenue(
    businessId: string,
    startDate: Timestamp,
    endDate: Timestamp,
  ): Promise<number> {
    const memberships = await getAggregate(
      query(
        this.businessMembershipRef(firebaseDb),
        where("business_id", "==", businessId),
        where("created_at", ">=", startDate),
        where("created_at", "<=", endDate),
      ),
      {
        totalRevenue: sum("price"),
      },
    );

    return memberships.data().totalRevenue ?? 0;
  }
}

export const membershipService = new MembershipService();
